// utils/migrateCollection.js
import { v4 as uuidv4 } from 'uuid';

/**
 * Migrates old indexed localStorage collection items to the new JSON array format.
 * Safe to run multiple times - checks for existing migrations.
 * 
 * Old format: title0, docLink0, itemNote0, etc. with collectionNumber counter
 * New format: userCollection JSON array with structured objects
 */
export const migrateOldCollection = () => {
  try {
    // Check if migration already happened
    const migrationComplete = localStorage.getItem('collectionMigrated');
    if (migrationComplete === 'true') {
      // console.log('Collection already migrated');
      return { migrated: false, reason: 'already_done' };
    }

    // Get the old collection counter
    const collectionNumber = parseInt(localStorage.getItem('collectionNumber') || '0');
    
    if (collectionNumber === 0) {
      // console.log('No old collection items to migrate');
      localStorage.setItem('collectionMigrated', 'true');
      return { migrated: false, reason: 'no_items' };
    }

    // Get existing new format collection (in case some items already exist)
    const existingCollection = JSON.parse(localStorage.getItem('userCollection') || '[]');
    const existingTitles = new Set(existingCollection.map(item => item.title));

    const migratedItems = [];
    let migratedCount = 0;
    let skippedCount = 0;

    // Iterate through old format items
    // Old format stores from index 1 to collectionNumber
    for (let i = collectionNumber; i >= 1; i--) {
      const title = localStorage.getItem(`title${i}`);
      
      // Skip if no title (empty slot) or already exists in new collection
      if (!title || title === 'null' || title === 'undefined') {
        continue;
      }

      // Skip duplicates
      if (existingTitles.has(title)) {
        skippedCount++;
        continue;
      }

      // Helper to get clean value (handle 'null' strings)
      const getCleanValue = (key) => {
        const value = localStorage.getItem(key);
        return (value && value !== 'null' && value !== 'undefined') ? value : '';
      };

      // Build the new format item
      const migratedItem = {
        id: `migrated_${i}_${uuidv4()}`, // Prefix helps identify migrated items
        title: title,
        url: getCleanValue(`detailLink${i}`),
        docLink: getCleanValue(`docLink${i}`),
        note: getCleanValue(`itemNote${i}`),
        date: getCleanValue(`itemDate${i}`),
        feedName: getCleanValue(`feedName${i}`),
        branch: getCleanValue(`branchName${i}`),
        topic: getCleanValue(`topicName${i}`),
        description: getCleanValue(`itemDescription${i}`),
        savedAt: new Date().toISOString(),
        migratedFrom: 'legacy', // Mark as migrated for debugging
        originalIndex: i
      };

      migratedItems.push(migratedItem);
      existingTitles.add(title);
      migratedCount++;
    }

    // Merge with existing collection (migrated items go to end, sorted by savedAt later)
    const mergedCollection = [...existingCollection, ...migratedItems];
    
    // Save the merged collection
    localStorage.setItem('userCollection', JSON.stringify(mergedCollection));
    
    // Mark migration as complete
    localStorage.setItem('collectionMigrated', 'true');
    localStorage.setItem('collectionMigratedAt', new Date().toISOString());

    // console.log(`Migration complete: ${migratedCount} items migrated, ${skippedCount} duplicates skipped`);
    
    return { 
      migrated: true, 
      count: migratedCount, 
      skipped: skippedCount,
      total: mergedCollection.length 
    };

  } catch (error) {
    console.error('Migration failed:', error);
    return { migrated: false, reason: 'error', error: error.message };
  }
};

/**
 * Optional: Clean up old localStorage keys after confirming migration worked
 * Only call this after you've verified the migration is successful
 */
export const cleanupOldCollectionKeys = () => {
  try {
    const collectionNumber = parseInt(localStorage.getItem('collectionNumber') || '0');
    
    const keysToRemove = [];
    for (let i = 0; i <= collectionNumber; i++) {
      keysToRemove.push(
        `title${i}`, `detailLink${i}`, `docLink${i}`, `itemDate${i}`,
        `itemNote${i}`, `feedName${i}`, `branchName${i}`, `topicName${i}`,
        `itemDescription${i}`
      );
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem('collectionNumber');
    localStorage.setItem('oldCollectionCleaned', 'true');
    
    // console.log(`Cleaned up ${keysToRemove.length} old localStorage keys`);
    return true;
  } catch (error) {
    console.error('Cleanup failed:', error);
    return false;
  }
};

/**
 * Force re-migration (useful for testing or if something went wrong)
 */
export const resetMigrationFlag = () => {
  localStorage.removeItem('collectionMigrated');
  localStorage.removeItem('collectionMigratedAt');
  // console.log('Migration flag reset - migration will run again on next call');
};